import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikContext } from "formik";
import { useParams } from "react-router-dom";

import SharesAllocationTable from "./ShareAllocationTable";
import ShareAllocationSelection from "./ShareAllocationSelection";
import { resetSharesTable } from "state/slices/tables/sharesAllocationTable";
import { hasManualAllocation } from "state/slices/manualShareAllocation";
import Button from "components/lib/Shared/Button";

const MainShareAllocationComponent = ({
  currentOfficialId,
  isCorporate,
  resolvedFields = [],
  disabled,
  isInManualUpdate,
  transactions,
  isInChangesList,
  remainderAmounts,
  row,
  editableShareAlloc: initialEditableShareAlloc = null,
  autoOpenSelection = false,
  onSubmissionComplete,
  onEditManualAllocation,
  onClearManualAllocation,
  showEditOnly = false, // New prop to show only edit functionality
  changesListData, // New prop to pass changes list data
}) => {
  const officialFormContext = useFormikContext();
  const dispatch = useDispatch();
  const { companyId } = useParams();

  // Initialize state based on autoOpenSelection and initialEditableShareAlloc
  const [showAllocateSharesBtn, setAllocateSharesBtn] = useState(
    !(initialEditableShareAlloc || autoOpenSelection)
  );
  const [editableShareAlloc, setEditableShareAlloc] = useState(
    initialEditableShareAlloc
  );
  const [selectedClassOfShare, setSelectedClassOfShare] = useState("");

  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );

  // Memoize classOfSharesOptions to ensure stable reference
  const classOfSharesOptions = useMemo(() => {
    return nominalShareCapital.map(({ sharesCategory }) => ({
      label: sharesCategory,
      value: sharesCategory,
    }));
  }, [nominalShareCapital]);

  const { shares } = useSelector((state) => state.sharesAllocationTableSlice);
  const alreadyTakenClassOfShares = useMemo(
    () => shares.map((share) => share.classOfShares),
    [shares]
  );

  // Check if localStorage has data to determine if allocation is complete
  const manualTx = useMemo(() => {
    if (!isInManualUpdate || !transactions) return null;
    if (Array.isArray(transactions)) {
      return (
        transactions.find((t) => t?.transactionType === "MANUAL") ||
        transactions[0] ||
        null
      );
    }
    return transactions;
  }, [isInManualUpdate, transactions]);

  // Check if Redux has allocation data
  const hasReduxAllocationData = useSelector((state) =>
    hasManualAllocation(state, companyId, currentOfficialId, manualTx?._id)
  );

  // Sync with parent's autoOpenSelection and initialEditableShareAlloc
  useEffect(() => {
    if (isInManualUpdate && hasReduxAllocationData) {
      // If we have Redux allocation data, always show the table
      setAllocateSharesBtn(true);
    } else {
      // For both manual and non-manual modes, show table by default
      // Only show form when explicitly requested via initialEditableShareAlloc or autoOpenSelection
      setAllocateSharesBtn(!(initialEditableShareAlloc || autoOpenSelection));
    }
  }, [
    initialEditableShareAlloc,
    autoOpenSelection,
    isInManualUpdate,
    hasReduxAllocationData,
  ]);

  // Additional effect to handle autoOpenSelection changes specifically
  useEffect(() => {
    // When autoOpenSelection changes, update showAllocateSharesBtn accordingly
    if (autoOpenSelection) {
      setAllocateSharesBtn(false); // Show form
    } else if (!initialEditableShareAlloc) {
      setAllocateSharesBtn(true); // Show table (only if not editing)
    }
  }, [autoOpenSelection, initialEditableShareAlloc]);

  // Reset state when currentOfficialId changes
  useEffect(() => {
    if (!currentOfficialId) {
      dispatch(resetSharesTable());
      setAllocateSharesBtn(true);
      setEditableShareAlloc(null);
      setSelectedClassOfShare("");
    }
  }, [currentOfficialId, dispatch]);

  // Helper function to compute remaining shares for a change
  const computeRemainingForChange = useCallback(
    (change, currentShares = []) => {
      if (!change) return 0;

      // For manual updates, use form data if available, otherwise use transaction remainder
      if (isInManualUpdate) {
        // Check form data first (from officialFormContext)
        const formShares = officialFormContext?.values?.sharesAllotted || [];
        if (formShares.length > 0) {
          const allocatedInForm = formShares
            .filter((share) => share.classOfShares === change.classOfShares)
            .reduce((sum, share) => sum + Number(share.allottedShares || 0), 0);
          return Math.max(
            0,
            Number(change.sharesOnOffer || 0) - allocatedInForm
          );
        }

        // Fallback to transaction remainder if available
        if (typeof change.remainder === "number") {
          return change.remainder;
        }
      }

      // Calculate from provided currentShares (fallback)
      const allocatedInTable = currentShares
        .filter((share) => share.classOfShares === change.classOfShares)
        .reduce((sum, share) => sum + Number(share.allottedShares || 0), 0);

      return Math.max(0, Number(change.sharesOnOffer || 0) - allocatedInTable);
    },
    [isInManualUpdate, officialFormContext]
  );

  // Sync editableShareAlloc with parent's prop
  useEffect(() => {
    if (
      initialEditableShareAlloc &&
      initialEditableShareAlloc !== editableShareAlloc
    ) {
      setEditableShareAlloc(initialEditableShareAlloc);
      setAllocateSharesBtn(false);
    }
  }, [initialEditableShareAlloc, editableShareAlloc]);

  // Handle classOfShares selection
  useEffect(() => {
    if (editableShareAlloc?.classOfShares) {
      const selectedOption = classOfSharesOptions.find(
        (c) => c.value === editableShareAlloc.classOfShares
      );
      if (selectedOption && selectedOption !== selectedClassOfShare) {
        setSelectedClassOfShare(selectedOption);
      }
    }
    // Only reset touched state if necessary
    if (Object.keys(officialFormContext.touched).length > 0) {
      officialFormContext.setTouched({}, false);
    }
  }, [
    editableShareAlloc,
    classOfSharesOptions,
    officialFormContext,
    selectedClassOfShare,
  ]);

  const availableClassOfSharesOptions = useMemo(() => {
    if (isInManualUpdate && manualTx?.shareCapitalChanges) {
      // For manual updates, filter based on remaining balance
      return manualTx.shareCapitalChanges
        .filter((change) => {
          const remaining = computeRemainingForChange(change);
          const isBeingEdited =
            editableShareAlloc &&
            editableShareAlloc.classOfShares === change.classOfShares;

          // Include if has remaining balance or is currently being edited
          return remaining > 0 || isBeingEdited;
        })
        .map((change) => ({
          label: `${change.classOfShares} (${computeRemainingForChange(
            change
          )} remaining)`,
          value: change.classOfShares,
          remaining: computeRemainingForChange(change),
        }));
    }

    // Non-manual mode - use original logic
    return classOfSharesOptions.filter(
      (option) =>
        !alreadyTakenClassOfShares.includes(option.value) ||
        (editableShareAlloc &&
          option.value === editableShareAlloc.classOfShares)
    );
  }, [
    classOfSharesOptions,
    alreadyTakenClassOfShares,
    editableShareAlloc,
    isInManualUpdate,
    manualTx,
    computeRemainingForChange,
  ]);

  // Determine whether to show the selection form
  const showShareAllocationSelection =
    !showAllocateSharesBtn || !!editableShareAlloc;

  // Handle submission completion
  const handleSubmissionComplete = (updatedData) => {
    // Always close the form and show the table after submission
    setAllocateSharesBtn(true);
    setEditableShareAlloc(null);
    setSelectedClassOfShare("");

    // Notify parent component
    if (onSubmissionComplete) {
      onSubmissionComplete(updatedData);
    }
  };
  const closeFormToTable = () => {
    setAllocateSharesBtn(true);
    setEditableShareAlloc(null);
    setSelectedClassOfShare("");
  };

  const handleAllotManually = () => {
    setAllocateSharesBtn(false);
    setEditableShareAlloc(null); // Ensure form is fresh
  };

  return (
    <div className="space-y-4">
      {showShareAllocationSelection ? (
        <ShareAllocationSelection
          setAllocateSharesBtn={setAllocateSharesBtn}
          editableShareAlloc={editableShareAlloc}
          setEditableShareAlloc={setEditableShareAlloc}
          currentOfficialId={currentOfficialId}
          classOfSharesOptions={availableClassOfSharesOptions}
          alreadyTakenClassOfShares={alreadyTakenClassOfShares}
          selectedClassOfShare={selectedClassOfShare}
          setSelectedClassOfShare={setSelectedClassOfShare}
          isInManualUpdate={isInManualUpdate}
          transactions={transactions}
          disabled={disabled}
          row={row}
          onCloseForm={closeFormToTable}
          remainderAmounts={remainderAmounts}
          onSubmissionComplete={handleSubmissionComplete}
          isInChangesList={isInChangesList}
        />
      ) : (
        <>
          <SharesAllocationTable
            setEditableShareAlloc={setEditableShareAlloc}
            isCorporate={isCorporate}
            resolvedFields={resolvedFields}
            showActionCol={!disabled}
            isInManualUpdate={isInManualUpdate}
            transactions={transactions}
            isInChangesList={isInChangesList}
            row={row}
            onEditManualAllocation={onEditManualAllocation}
            onClearManualAllocation={onClearManualAllocation}
            showEditOnly={showEditOnly}
            changesListData={changesListData}
            initiatedProcessType={initiatedProcessType}
          />
          {/* Conditional Allot Shares button - show for non-manual updates or manual updates with 2+ share classes */}
          {availableClassOfSharesOptions.length > 0 &&
            !disabled &&
            !changesListData && // Hide button when showing changes list data
            (!isInManualUpdate ||
              (isInManualUpdate &&
                manualTx?.shareCapitalChanges?.length >= 2)) && (
              <div className="flex justify-start">
                <Button
                  onClick={() => {
                    setAllocateSharesBtn(false);
                    setEditableShareAlloc(null); // Ensure form is fresh
                  }}
                  className="inline-flex justify-center px-4 py-2 mt-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Allot Shares
                </Button>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default MainShareAllocationComponent;
